/**
 * Utilities for managing Mixpanel device ID passed from main platform.
 * Handles device ID extraction from bridge cookie (preferred) or URL (legacy)
 * and storage before Mixpanel initialization.
 */

const DEVICE_ID_STORAGE_KEY = "auth:mixpanel_device_id";
const DEVICE_ID_BRIDGE_COOKIE = "mp_bridge_device_id";
const COOKIE_DOMAIN = (import.meta as any).env?.VITE_COOKIE_DOMAIN;

const getCookieValue = (name: string): string | null => {
	const escapedName = name.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
	const match = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${escapedName}=([^;]*)`),
	);
	return match ? decodeURIComponent(match[1]) : null;
};

const clearBridgeCookie = () => {
	const commonParts = ["Path=/", "Max-Age=0", "SameSite=Lax"];
	if (window.location.protocol === "https:") {
		commonParts.push("Secure");
	}
	const variants = [
		`${DEVICE_ID_BRIDGE_COOKIE}=; ${commonParts.join("; ")}`,
		COOKIE_DOMAIN
			? `${DEVICE_ID_BRIDGE_COOKIE}=; ${commonParts.join("; ")}; Domain=${COOKIE_DOMAIN}`
			: null,
	].filter(Boolean);

	for (const cookie of variants) {
		document.cookie = cookie as string;
	}
};

/**
 * Extract device ID from bridge cookie first, then URL parameter as legacy fallback.
 * Preferred source: `mp_bridge_device_id` cookie.
 * Legacy source: `mp_id` query parameter.
 *
 * @returns Device ID if found, null otherwise
 */
export function getDeviceIdFromUrl(): string | null {
	try {
		const bridgeDeviceId = getCookieValue(DEVICE_ID_BRIDGE_COOKIE);
		if (bridgeDeviceId) {
			console.log(
				"[MIXPANEL_DEVICE_ID] Extracted device ID from bridge cookie",
			);
			return bridgeDeviceId;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const deviceId = urlParams.get("mp_id");
		if (deviceId) {
			console.log("[MIXPANEL_DEVICE_ID] Extracted device ID from URL fallback");
		}
		return deviceId || null;
	} catch (error) {
		console.warn("[MIXPANEL_DEVICE_ID] Failed to extract device ID:", error);
		return null;
	}
}

/**
 * Store device ID before Mixpanel initialization.
 * This ensures the device ID from main platform is available when needed.
 *
 * @param deviceId - Device ID to store
 */
export function setMixpanelDeviceId(deviceId: string): void {
	try {
		sessionStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
		console.log("[MIXPANEL_DEVICE_ID] Stored device ID:", deviceId);
	} catch (error) {
		console.warn("[MIXPANEL_DEVICE_ID] Failed to store device ID:", error);
	}
}

/**
 * Retrieve stored device ID.
 * Returns the device ID that was passed from main platform.
 *
 * @returns Stored device ID if found, null otherwise
 */
export function getStoredDeviceId(): string | null {
	try {
		const stored = sessionStorage.getItem(DEVICE_ID_STORAGE_KEY);
		return stored || null;
	} catch (error) {
		console.warn(
			"[MIXPANEL_DEVICE_ID] Failed to retrieve stored device ID:",
			error,
		);
		return null;
	}
}

/**
 * Clear stored device ID.
 * Useful for cleanup after identity linking is complete.
 */
export function clearStoredDeviceId(): void {
	try {
		sessionStorage.removeItem(DEVICE_ID_STORAGE_KEY);
		console.log("[MIXPANEL_DEVICE_ID] Cleared stored device ID");
	} catch (error) {
		console.warn(
			"[MIXPANEL_DEVICE_ID] Failed to clear stored device ID:",
			error,
		);
	}
}

/**
 * Remove mp_id parameter from URL after it's been captured and stored.
 * Also clears the bridge cookie.
 * This prevents the parameter from being processed multiple times and
 * keeps the URL clean for privacy/security.
 */
export function removeMpIdFromUrl(): void {
	try {
		clearBridgeCookie();
		const url = new URL(window.location.href);
		if (url.searchParams.has("mp_id")) {
			url.searchParams.delete("mp_id");
			const newUrl =
				url.pathname +
				(url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") +
				url.hash;
			window.history.replaceState({}, "", newUrl);
			console.log("[MIXPANEL_DEVICE_ID] Removed mp_id from URL fallback");
		}
	} catch (error) {
		console.warn(
			"[MIXPANEL_DEVICE_ID] Failed to clean bridge cookie/mp_id URL:",
			error,
		);
	}
}
